import React, { useEffect, useState, useCallback } from "react";
import { Table, Pagination } from "rsuite";
import { fetchBlogs } from "../../../api/blogsAPI";
import { handleDeleteBlog } from "./deleteBlogHandler";
import ImageModal from "../../imageModal";
import ReadMoreModal from "../../readMoreModal";
import CommentModal from "./commentModal";
import BlogEditor from "./BlogEditor";
import { isValidUrl, placeholderImage } from "../../invalidImage";
import AdminPageHeader from "../_shared/AdminPageHeader";
import AdminSearchBar from "../_shared/AdminSearchBar";
import AdminCard from "../_shared/AdminCard";
import { EditButton, DeleteButton, CommentButton } from "../_shared/AdminActions";

const { Column, HeaderCell, Cell } = Table;

// ── View modes ────────────────────────────────────────────────
const VIEW = {
  LIST:  "list",
  ADD:   "add",
  EDIT:  "edit",
};

const BlogTable = () => {
  const [view, setView] = useState(VIEW.LIST);
  const [editingBlog, setEditingBlog] = useState(null);

  const [data, setData]             = useState([]);
  const [allData, setAllData]       = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [page, setPage]             = useState(1);
  const [limit, setLimit]           = useState(10);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const blogsData = await fetchBlogs();
      setAllData(blogsData);
      setData(blogsData);
    } catch (err) {
      console.error("Failed to fetch blogs:", err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const start = (page - 1) * limit;
    setDisplayData(data.slice(start, start + limit));
  }, [page, limit, data]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setPage(1);
    if (value) {
      setData(allData.filter((b) => b.title.toLowerCase().includes(value.toLowerCase())));
    } else {
      setData(allData);
    }
  };

  const handleEditorSuccess = () => {
    setView(VIEW.LIST);
    setEditingBlog(null);
    fetchData();
  };

  // ── Full-page editor view ─────────────────────────────────
  if (view === VIEW.ADD) {
    return (
      <div className="flex-1 min-h-screen">
        <BlogEditor
          onSuccess={handleEditorSuccess}
          onCancel={() => setView(VIEW.LIST)}
        />
      </div>
    );
  }

  if (view === VIEW.EDIT && editingBlog) {
    return (
      <div className="flex-1 min-h-screen">
        <BlogEditor
          blog={editingBlog}
          onSuccess={handleEditorSuccess}
          onCancel={() => { setView(VIEW.LIST); setEditingBlog(null); }}
        />
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────
  return (
    <div className="flex-1 min-h-screen bg-slate-50 overflow-y-auto">
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <AdminPageHeader
          title="Blog Management"
          subtitle="Kelola artikel blog Defra"
          badge={data.length}
          actionLabel="✍️ Tulis Blog Baru"
          onAction={() => setView(VIEW.ADD)}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          }
        />

        <AdminCard>
          <AdminSearchBar
            value={searchKeyword}
            onChange={handleSearch}
            placeholder="Cari berdasarkan judul..."
          />

          <div className="overflow-hidden rounded-2xl border border-slate-100">
            <Table autoHeight data={displayData} rowHeight={100}>
              <Column width={60} align="center" fixed>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">No</HeaderCell>
                <Cell>
                  {(rowData, rowIndex) => (
                    <span className="text-sm font-semibold text-slate-500">
                      {(page - 1) * limit + rowIndex + 1}
                    </span>
                  )}
                </Cell>
              </Column>

              <Column width={100}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Image</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <ImageModal imageUrl={isValidUrl(rowData.image) ? rowData.image : placeholderImage} />
                  )}
                </Cell>
              </Column>

              <Column width={280}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Judul</HeaderCell>
                <Cell>
                  {(rowData) => {
                    const truncated = rowData.title.length > 50
                      ? rowData.title.slice(0, 50) + "..."
                      : rowData.title;
                    return (
                      <ReadMoreModal text={rowData.title}>
                        <span className="font-semibold text-slate-800">{truncated}</span>
                      </ReadMoreModal>
                    );
                  }}
                </Cell>
              </Column>

              <Column flexGrow={1} minWidth={200}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Deskripsi</HeaderCell>
                <Cell>
                  {(rowData) => {
                    const stripped = (rowData.description || "").replace(/<[^>]*>/g, "");
                    const truncated = stripped.length > 100 ? stripped.slice(0, 100) + "..." : stripped;
                    return (
                      <ReadMoreModal text={rowData.description}>
                        <span className="text-sm text-slate-600">{truncated}</span>
                      </ReadMoreModal>
                    );
                  }}
                </Cell>
              </Column>

              <Column width={140}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Author</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-500 to-eco-500 flex items-center justify-center text-white text-xs font-bold">
                        {rowData.author?.charAt(0).toUpperCase() || "?"}
                      </div>
                      <span className="text-sm text-slate-700">{rowData.author}</span>
                    </div>
                  )}
                </Cell>
              </Column>

              <Column width={200}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Aksi</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <div className="flex items-center gap-2">
                      <EditButton
                        onClick={() => {
                          setEditingBlog(rowData);
                          setView(VIEW.EDIT);
                        }}
                      />
                      <DeleteButton onClick={() => handleDeleteBlog(rowData.id, data, setData)} />
                      <CommentButton
                        onClick={() => {
                          setSelectedBlog(rowData);
                          setIsCommentModalOpen(true);
                        }}
                      />
                    </div>
                  )}
                </Cell>
              </Column>
            </Table>
          </div>

          <div className="flex justify-end mt-5">
            <Pagination
              prev next first last ellipsis boundaryLinks
              maxButtons={5} size="xs"
              layout={["total", "-", "limit", "|", "pager"]}
              total={data.length}
              limitOptions={[5, 10, 20, 50]}
              limit={limit}
              activePage={page}
              onChangePage={setPage}
              onChangeLimit={(l) => { setLimit(l); setPage(1); }}
            />
          </div>
        </AdminCard>
      </div>

      {isCommentModalOpen && selectedBlog && (
        <CommentModal
          comments={selectedBlog.comments}
          onClose={() => setIsCommentModalOpen(false)}
        />
      )}
    </div>
  );
};

export default BlogTable;
